import { LevelType, PositionType, SpaceType } from 'data/types';
import { isAutoLayoutSpace } from 'utils/auto-layout-space';
import { filterConditionedSpaces } from 'utils/configurations';
import { polygonBoundingBox } from 'utils/footprint';
import {
  footprintBoundingBox,
  getEffectiveFootprintPolygons
} from 'utils/footprint-polygons';
import { isLevelConditioned } from 'utils/level-conditioning';

export type HybridZonedSpace = {
  space: SpaceType;
  level: LevelType;
  area: number;
};

export type HybridLevelGroup = {
  level: LevelType;
  leftovers: HybridZonedSpace[];
  userPlaced: HybridZonedSpace[];
};

export function hybridSpaceArea(space: SpaceType): number {
  const rawSquareFootage = Number(
    space.attributes.find((attr) => attr.code === 'square_footage')?.value
  );
  if (Number.isFinite(rawSquareFootage) && rawSquareFootage > 0) {
    return rawSquareFootage;
  }
  if (space.width && space.length) {
    return space.width * space.length;
  }
  return 0;
}

export function collectConditionedHybridSpaces(
  structure: LevelType[],
  autoLayoutSpaceIds: readonly string[]
): { leftovers: HybridZonedSpace[]; userPlaced: HybridZonedSpace[] } {
  const leftovers: HybridZonedSpace[] = [];
  const userPlaced: HybridZonedSpace[] = [];

  structure.forEach((level) => {
    if (!isLevelConditioned(level)) {
      return;
    }
    filterConditionedSpaces(
      level.sections.flatMap((section) => section.spaces)
    ).forEach((space) => {
      const entry = { space, level, area: hybridSpaceArea(space) };
      if (isAutoLayoutSpace(space, level, autoLayoutSpaceIds)) {
        leftovers.push(entry);
      } else {
        userPlaced.push(entry);
      }
    });
  });

  return { leftovers, userPlaced };
}

export function groupHybridSpacesByLevel(
  structure: LevelType[],
  leftovers: HybridZonedSpace[],
  userPlaced: HybridZonedSpace[]
): HybridLevelGroup[] {
  const byLevel = new Map<string, HybridLevelGroup>();

  leftovers.forEach((entry) => {
    const existing = byLevel.get(entry.level.uniqueId);
    if (existing) {
      existing.leftovers.push(entry);
    } else {
      byLevel.set(entry.level.uniqueId, {
        level: entry.level,
        leftovers: [entry],
        userPlaced: []
      });
    }
  });

  userPlaced.forEach((entry) => {
    const existing = byLevel.get(entry.level.uniqueId);
    if (existing) {
      existing.userPlaced.push(entry);
    } else {
      byLevel.set(entry.level.uniqueId, {
        level: entry.level,
        leftovers: [],
        userPlaced: [entry]
      });
    }
  });

  return structure
    .map((level) => byLevel.get(level.uniqueId))
    .filter((group): group is HybridLevelGroup => group !== undefined);
}

export function pickLargestHybridSpace(
  entries: HybridZonedSpace[]
): HybridZonedSpace {
  return entries.reduce((best, entry) =>
    entry.area > best.area ? entry : best
  );
}

export function hybridLevelBoundingBox(
  level: LevelType,
  spaces: SpaceType[]
): ReturnType<typeof polygonBoundingBox> | null {
  const footprintBounds = footprintBoundingBox(
    getEffectiveFootprintPolygons(level)
  );
  if (footprintBounds) {
    return footprintBounds;
  }
  const corners: PositionType[] = spaces.flatMap((space) =>
    space.position
      ? [
          space.position,
          {
            x: space.position.x + (space.width ?? 0),
            y: space.position.y + (space.length ?? 0)
          }
        ]
      : []
  );
  if (corners.length === 0) return null;
  return polygonBoundingBox(corners);
}
